import type { AgentCarrierCommissionDto } from '../../base/common/dtos/agent-carrier-commission.dto';
import type {
  AddCarrierDto,
  CarrierDto,
  CarrierListDto,
  UpdateCarrierDto,
} from '../../base/common/dtos/carrier-list.dto';
import type { SaveAgentCarrierCommissionDto } from '../../base/common/dtos/save-agent-carrier-commission.dto';
import type { BaseDataTableUrlParamsModel } from '../../base/common/models/base/base-data-table-url-params.model';
import appendUrlDataTableParamsT from '../../base/utils/append-url-data-table-params.t';

interface State {
  loading: boolean;
  carriers: CarrierListDto;
  selectedCarrierId: number | null;
  selectedCarrier: CarrierDto | null;
  carrierSelectSource: CarrierDto[];
}

export interface FetchCarriersListParams extends BaseDataTableUrlParamsModel<CarrierDto> {}

export const useCarriersStore = defineStore('carriersStore', {
  state: (): State => ({
    loading: false,
    carriers: {
      data: [],
      total: 0,
    },
    selectedCarrierId: null,
    selectedCarrier: null,
    carrierSelectSource: [],
  }),
  actions: {
    async fetchCarriersList(options?: FetchCarriersListParams): Promise<void> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;

      let url = 'catalogs/contacts/datatable/carriers';

      if (options) {
        url = appendUrlDataTableParamsT(url, options);
      }

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        this.carriers.data = response.data.list;
        this.carriers.total = response.data.count;
      } catch {
        $toastError('Failed to load carriers');
      } finally {
        this.loading = false;
      }
    },
    async removeSelectedCarrier(): Promise<void> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers/${this.selectedCarrierId}`;

      try {
        await $snapApi({
          method: 'DELETE',
          url,
        });
      } catch {
        $toastError('Failed to remove carrier');
      } finally {
        this.loading = false;
      }

      this.selectedCarrierId = null;
    },
    async fetchSelectedCarrier(): Promise<void> {
      if (!this.selectedCarrierId) {
        return;
      }

      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers/${this.selectedCarrierId}`;

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        this.selectedCarrier = response.data;
      } catch {
        $toastError('Failed to load carrier details');
      } finally {
        this.loading = false;
      }
    },
    async saveNew(model: AddCarrierDto): Promise<void> {
      const body = model;
      body.firstName = 'Carrier';
      body.lastName = 'Carrier';

      const { $snapApi, $toastError, $toastSuccess } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers`;

      try {
        await $snapApi({
          method: 'POST',
          url,
          data: body,
        });

        $toastSuccess('A new Carrier/MGA is saved');
      } catch {
        $toastError('Failed to save a new Carrier/MGA');
      } finally {
        this.loading = false;
      }
    },
    async saveExisting(model: UpdateCarrierDto): Promise<void> {
      const body = model;
      body.firstName = 'Carrier';
      body.lastName = 'Carrier';

      const { $snapApi, $toastError, $toastSuccess } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers/${this.selectedCarrierId}`;

      try {
        await $snapApi({
          method: 'PUT',
          url,
          data: body,
        });

        $toastSuccess('An existing Carrier/MGA is saved');
      } catch {
        $toastError('Failed to save a Carrier/MGA');
      } finally {
        this.loading = false;
        this.selectedCarrierId = null;
      }
    },
    async fetchCarrierSelectSource(): Promise<void> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers`;

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        this.carrierSelectSource = response.data;
      } catch {
        $toastError('Failed to load carrier options');
      } finally {
        this.loading = false;
      }
    },
    async fetchAgentCarrierCommission(id: number): Promise<AgentCarrierCommissionDto> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers/${id}/commission`;

      try {
        const response = await $snapApi({
          method: 'GET',
          url,
        });

        return response.data;
      } catch (e) {
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async saveAgentCarrierCommission(data: SaveAgentCarrierCommissionDto): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      const url = `catalogs/contacts/carriers/${data.carrierId}/commission`;

      try {
        await $snapApi({
          method: 'PUT',
          url,
          data,
        });

        const carrier = this.carriers.data.find(({ id }) => id === data.carrierId);
        if (carrier) {
          carrier.commission = data.commission;
        }

        return;
      } catch (e) {
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
});
